import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../../i18n/context';
import ToolboxReturnPanel from '../../components/ToolboxReturnPanel';
import RebateBanner from '../../components/RebateBanner';
import './SupportResistancePage.css';

const POPULAR_SYMBOLS = ['BTC', 'ETH', 'SOL', 'BNB', 'DOGE', 'XRP', 'ADA', 'AVAX', 'LINK'];

function getIntervals(t) {
  return [
    { value: '15m', title: t('levels.interval15m'), desc: '15m' },
    { value: '1h', title: t('levels.interval1h'), desc: '1H' },
    { value: '4h', title: t('levels.interval4h'), desc: '4H' },
    { value: '1d', title: t('levels.interval1d'), desc: '1D' },
  ];
}

function getToolLinks(t) {
  return [
    { label: t('levels.toolPatternMatch'), href: '/pattern-match' },
    { label: t('levels.toolDumpDetective'), href: '/dump-detective' },
  ];
}

function formatUsd(value) {
  if (!Number.isFinite(Number(value))) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: Number(value) >= 1000 ? 2 : 6,
  }).format(Number(value));
}

function formatDistance(value) {
  if (!Number.isFinite(Number(value))) return '--';
  const number = Number(value);
  return `${number >= 0 ? '+' : ''}${number.toFixed(2)}%`;
}

function formatTime(value) {
  if (!value) return '--';
  return new Date(value).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCooldown(seconds, t) {
  const safeSeconds = Math.max(1, Number(seconds) || 1);
  const minutes = Math.floor(safeSeconds / 60);
  const rest = safeSeconds % 60;
  if (!minutes) return `${rest} ${t('levels.seconds')}`;
  return `${minutes} ${t('levels.minutes')} ${rest} ${t('levels.seconds')}`;
}

function strengthClass(strength) {
  if (strength === '强' || strength === 'strong') return 'strong';
  if (strength === '中' || strength === 'medium') return 'medium';
  return 'weak';
}

function LevelList({ title, levels, empty, tone }) {
  const { t } = useLang();
  return (
    <section className="sr-panel sr-level-panel">
      <div className="sr-section-head">
        <h2>{title} <span>({levels.length})</span></h2>
        <p>{t('levels.priceCloserTip')}</p>
      </div>
      <div className="sr-level-table">
        <div className="sr-level-row sr-level-header">
          <span>{t('levels.price')}</span>
          <span>{t('levels.distance')}</span>
          <span>{t('levels.strength')}</span>
          <span>{t('levels.touches')}</span>
        </div>
        {levels.length ? levels.map((level) => (
          <div className="sr-level-row" key={`${level.side}-${level.price}`}>
            <strong>{formatUsd(level.price)}</strong>
            <span className={level.distancePct >= 0 ? 'sr-up' : 'sr-down'}>{formatDistance(level.distancePct)}</span>
            <em className={`${tone} ${strengthClass(level.strength)}`}>{t('levels.strength_' + level.strength)}</em>
            <span>{t('levels.touchesCount', { count: level.touches })}</span>
          </div>
        )) : (
          <div className="sr-empty-row">{empty}</div>
        )}
      </div>
    </section>
  );
}

function ResultOverview({ result }) {
  const { t } = useLang();
  const rateText = result.rateLimit?.enabled
    ? t('levels.rateLimitRemaining', { remaining: result.rateLimit.remaining, limit: result.rateLimit.limit })
    : t('levels.rateLimitDisabled');

  return (
    <section className="sr-result-grid">
      <article className="sr-panel sr-price-card">
        <span>{t('levels.currentPrice')}</span>
        <strong>{formatUsd(result.currentPrice)}</strong>
        <p>{result.pair} · {formatTime(result.updatedAt)} {t('levels.updated')}</p>
      </article>
      <article className="sr-panel sr-meta-card">
        <span>{t('levels.queryInterval')}</span>
        <strong>{result.interval?.toUpperCase()}</strong>
        <p>{t('levels.binanceKlineTip')}</p>
      </article>
      <article className="sr-panel sr-meta-card">
        <span>{t('levels.queryQuota')}</span>
        <strong>{rateText}</strong>
        <p>{t('levels.quotaCooldownTip')}</p>
      </article>
    </section>
  );
}

function SupportResistancePage() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [symbol, setSymbol] = useState('BTC');
  const [interval, setInterval] = useState('1h');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(null);

  const normalizedSymbol = useMemo(() => symbol.trim().toUpperCase(), [symbol]);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setCooldown(null);

    if (!POPULAR_SYMBOLS.includes(normalizedSymbol)) {
      setError(t('levels.unsupportedSymbol'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/support-resistance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol: normalizedSymbol, interval }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 429) {
          setCooldown(data.retryAfterSeconds || data.rateLimit?.retryAfterSeconds || 60);
        }
        throw new Error(data.error || t('levels.queryFailed'));
      }

      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="sr-page">
      <header className="sr-hero">
        <button type="button" className="sr-back-link" onClick={() => navigate('/tools')}>{t('toolbox.backToTools')}</button>
        <p className="sr-eyebrow">Support / Resistance</p>
        <h1>{t('levels.pageTitle')}</h1>
        <p>
          {t('levels.pageDesc')}
        </p>
        <div className="sr-hero-meta">
          <span>Beta</span>
          <span>Binance</span>
          <span>{t('levels.multiInterval')}</span>
        </div>
      </header>

      <form className="sr-search-panel" onSubmit={submit}>
        <div className="sr-form-top">
          <label className="sr-symbol-field">
            <span>{t('levels.symbol')}</span>
            <input
              value={symbol}
              onChange={(event) => setSymbol(event.target.value)}
              placeholder="BTC"
              autoComplete="off"
            />
          </label>
          <button className="sr-primary" type="submit" disabled={loading}>
            {loading ? t('levels.querying') : t('levels.queryBtn')}
          </button>
        </div>

        <div className="sr-popular">
          <span>{t('levels.popular')}</span>
          {POPULAR_SYMBOLS.slice(0, 6).map((item) => (
            <button
              key={item}
              type="button"
              className={normalizedSymbol === item ? 'active' : ''}
              onClick={() => setSymbol(item)}
            >
              {item}
            </button>
          ))}
        </div>

        <div className="sr-interval-grid" role="group" aria-label={t('levels.selectInterval')}>
          {getIntervals(t).map((item) => (
            <button
              key={item.value}
              type="button"
              className={interval === item.value ? 'active' : ''}
              onClick={() => setInterval(item.value)}
            >
              <strong>{item.title}</strong>
              <span>{item.desc}</span>
            </button>
          ))}
        </div>

        <section className="sr-query-note">
          <strong>{t('levels.querySettings')}</strong>
          <p>{t('levels.querySettingsDesc')}</p>
        </section>

        {error && (
          <div className="sr-error" role="alert">
            {error}
            {cooldown && <span>{t('levels.pleaseWait', { time: formatCooldown(cooldown, t) })}</span>}
          </div>
        )}
      </form>

      {result && (
        <>
          <ResultOverview result={result} />

          <section className="sr-level-grid">
            <LevelList
              title={t('levels.resistance')}
              levels={result.resistances || []}
              empty={t('levels.noResistances')}
              tone="resistance"
            />
            <LevelList
              title={t('levels.support')}
              levels={result.supports || []}
              empty={t('levels.noSupports')}
              tone="support"
            />
          </section>

          <LevelList
            title={t('levels.nearby')}
            levels={result.nearby || []}
            empty={t('levels.noNearby')}
            tone="nearby"
          />

          <section className="sr-panel sr-explain">
            <h2>{t('levels.dataDisclaimer')}</h2>
            <p>{t('levels.dataDisclaimerDesc')}</p>
            <p>{t('levels.notInvestAdvice')}</p>
          </section>
        </>
      )}

      <ToolboxReturnPanel
        className="sr-toolbox-return"
        description={t('levels.toolboxDesc')}
        links={getToolLinks(t)}
      />

      <RebateBanner />
    </main>
  );
}

export default SupportResistancePage;
